import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import SignatureCanvas from 'react-signature-canvas';
import { PDFDocument, rgb } from 'pdf-lib';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PDFSigner = () => {
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [showSignature, setShowSignature] = useState(false);
  const [signatures, setSignatures] = useState([]); // Array of signatures
  const [isPlacingSignature, setIsPlacingSignature] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [ipAddress, setIpAddress] = useState('');
  const [isSigning, setIsSigning] = useState(false);
  const [pageScale, setPageScale] = useState(1);
  
  // Email notification settings
  const [notifyEmail, setNotifyEmail] = useState('');
  const [nextSignerEmail, setNextSignerEmail] = useState('');
  const [nextSignerName, setNextSignerName] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  
  const signatureRef = useRef();
  const pdfContainerRef = useRef();

  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then(response => response.json())
      .then(data => setIpAddress(data.ip))
      .catch(() => setIpAddress('Unable to fetch IP'));
  }, []);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      const url = URL.createObjectURL(file);
      setPdfUrl(url);
      setPageNumber(1);
      setShowSignature(false);
      setSignatureData(null);
      setSignatures([]); // Reset all signatures
      setIsPlacingSignature(false);
    } else {
      alert('Please upload a valid PDF file');
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleClearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const handleSaveSignature = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      const dataUrl = signatureRef.current.toDataURL();
      setSignatureData(dataUrl);
      setShowSignature(false);
      setIsPlacingSignature(true);
    } else {
      alert('Please draw your signature');
    }
  };

  const handlePlaceSignature = (e) => {
    if (!isPlacingSignature || !pdfContainerRef.current || !signatureData) return;
    
    const rect = pdfContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Add new signature to the current page
    const newSignature = {
      id: Date.now(),
      data: signatureData,
      x: x,
      y: y,
      page: pageNumber,
      width: 200,
      height: 100
    };
    
    setSignatures(prev => [...prev, newSignature]);
    setIsPlacingSignature(false);
  };

  const removeSignature = (signatureId) => {
    setSignatures(prev => prev.filter(sig => sig.id !== signatureId));
  };

  const addNewSignature = () => {
    if (!signatureData) {
      setShowSignature(true);
      return;
    }
    setIsPlacingSignature(true);
  };

  const handleSubmitSignedPDF = async () => {
    if (!pdfFile || signatures.length === 0 || !signerName || !signerEmail) {
      alert('Please complete all fields and add at least one signature');
      return;
    }

    setIsSigning(true);

    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const pages = pdfDoc.getPages();
      
      // Embed signature image once
      const signatureImage = await pdfDoc.embedPng(signatureData);
      
      // Process each signature
      for (const signature of signatures) {
        const page = pages[signature.page - 1];
        const pageHeight = page.getHeight();
        
        // Scale signature to fit
        const dims = signatureImage.scale(0.3);
        
        // Calculate Y position (PDF coordinates are bottom-up)
        const adjustedY = pageHeight - signature.y - dims.height;
        
        // Draw signature
        page.drawImage(signatureImage, {
          x: signature.x,
          y: adjustedY,
          width: dims.width,
          height: dims.height,
        });
        
        // Add signature metadata
        const timestamp = new Date();
        page.drawText(`Signed by: ${signerName}`, {
          x: signature.x,
          y: adjustedY - 15,
          size: 8,
          color: rgb(0, 0, 0),
        });
        
        page.drawText(`Date: ${timestamp.toLocaleDateString()} ${timestamp.toLocaleTimeString()}`, {
          x: signature.x,
          y: adjustedY - 28,
          size: 8,
          color: rgb(0, 0, 0),
        });
        
        page.drawText(`IP: ${ipAddress}`, {
          x: signature.x,
          y: adjustedY - 41,
          size: 8,
          color: rgb(0, 0, 0),
        });
      }
      
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      
      const timestamp = new Date().toISOString();
      const formData = new FormData();
      formData.append('pdf', blob, `signed_${pdfFile.name}`);
      formData.append('signerName', signerName);
      formData.append('signerEmail', signerEmail);
      formData.append('ipAddress', ipAddress);
      formData.append('timestamp', timestamp);
      formData.append('originalFileName', pdfFile.name);
      formData.append('signatureCount', signatures.length);
      
      // Add notification data
      if (notifyEmail) {
        formData.append('notifyEmail', notifyEmail);
      }
      if (nextSignerEmail) {
        formData.append('nextSignerEmail', nextSignerEmail);
        formData.append('nextSignerName', nextSignerName);
        formData.append('message', notificationMessage);
      }
      
      const response = await fetch('/api/sign-pdf', {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        await response.json();
        
        const downloadBlob = new Blob([pdfBytes], { type: 'application/pdf' });
        const downloadUrl = URL.createObjectURL(downloadBlob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `signed_${pdfFile.name}`;
        a.click();
        URL.revokeObjectURL(downloadUrl);
        
        alert('PDF signed successfully! The signed document has been downloaded.');
        
        setPdfFile(null);
        setPdfUrl(null);
        setSignatureData(null);
        setSignatures([]);
        setSignerName('');
        setSignerEmail('');
        setShowSignature(false);
        setNotifyEmail('');
        setNextSignerEmail('');
        setNextSignerName('');
        setNotificationMessage('');
      } else {
        throw new Error('Failed to save signed PDF');
      }
    } catch (error) {
      console.error('Error signing PDF:', error);
      alert('Error signing PDF. Please try again.');
    } finally {
      setIsSigning(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 mb-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link 
                to="/"
                className="text-2xl font-bold text-forest-800 hover:text-forest-700"
              >
                Seen Place Hub
              </Link>
              <span className="text-gray-400">/</span>
              <h1 className="text-xl font-semibold text-gray-900">PDF Document Signer</h1>
            </div>
            
            <Link
              to="/"
              className="text-sm text-sage-600 hover:text-sage-700 font-medium"
            >
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4">
        
        {!pdfFile && (
          <div className="bg-white rounded-lg shadow-md p-8">
            <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <svg className="w-8 h-8 mb-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">PDF files only</p>
              </div>
              <input type="file" className="hidden" accept=".pdf" onChange={handleFileUpload} />
            </label>
          </div>
        )}
        
        {pdfFile && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="mb-4 flex justify-between items-center">
                  <div>
                    <button
                      onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                      disabled={pageNumber <= 1}
                      className="px-3 py-1 bg-blue-500 text-white rounded disabled:bg-gray-300 mr-2"
                    >
                      Previous
                    </button>
                    <span className="mx-2">
                      Page {pageNumber} of {numPages}
                    </span>
                    <button
                      onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                      disabled={pageNumber >= numPages}
                      className="px-3 py-1 bg-blue-500 text-white rounded disabled:bg-gray-300 ml-2"
                    >
                      Next
                    </button>
                  </div>
                  <div className="flex space-x-2">
                    {signatures.length > 0 && (
                      <button
                        onClick={() => setSignatures([])}
                        className="px-3 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
                      >
                        Clear All Signatures
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setPdfFile(null);
                        setPdfUrl(null);
                        setSignatureData(null);
                        setSignatures([]);
                        setIsPlacingSignature(false);
                      }}
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      Remove PDF
                    </button>
                  </div>
                </div>
                
                <div 
                  ref={pdfContainerRef}
                  className="relative border border-gray-300 overflow-auto"
                  onClick={handlePlaceSignature}
                  style={{ cursor: isPlacingSignature ? 'crosshair' : 'default' }}
                >
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<div className="p-4">Loading PDF...</div>}
                    error={<div className="p-4 text-red-600">Error loading PDF. Please try again.</div>}
                  >
                    <Page 
                      pageNumber={pageNumber} 
                      width={600}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      onRenderSuccess={() => setPageScale(600)}
                    />
                  </Document>
                  
                  {/* Show all signatures for current page */}
                  {signatures
                    .filter(sig => sig.page === pageNumber)
                    .map((signature, index) => (
                      <div
                        key={signature.id}
                        className="absolute border-2 border-blue-400 bg-blue-50 bg-opacity-50"
                        style={{
                          left: `${signature.x}px`,
                          top: `${signature.y}px`,
                          width: `${signature.width}px`,
                          height: `${signature.height}px`,
                          pointerEvents: 'auto'
                        }}
                      >
                        <img
                          src={signature.data}
                          alt={`Signature ${index + 1}`}
                          className="w-full h-full object-contain"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSignature(signature.id);
                          }}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600 flex items-center justify-center"
                        >
                          ×
                        </button>
                        <div className="absolute -bottom-6 left-0 text-xs text-gray-600 bg-white px-1 rounded">
                          Page {signature.page}
                        </div>
                      </div>
                    ))}
                </div>
                
                {isPlacingSignature && (
                  <div className="mt-2 p-2 bg-blue-100 text-blue-700 rounded">
                    Click on the PDF where you want to place your signature
                  </div>
                )}
              </div>
            </div>
            
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
                <h2 className="text-xl font-semibold">Signer Information</h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={signerEmail}
                    onChange={(e) => setSignerEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your email"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IP Address
                  </label>
                  <input
                    type="text"
                    value={ipAddress}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timestamp
                  </label>
                  <input
                    type="text"
                    value={new Date().toLocaleString()}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                  />
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-lg font-semibold mb-2">Signatures ({signatures.length})</h3>
                  
                  {/* Signature List */}
                  {signatures.length > 0 && (
                    <div className="mb-4 space-y-2 max-h-40 overflow-y-auto">
                      {signatures.map((sig, index) => (
                        <div key={sig.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm">Signature {index + 1} (Page {sig.page})</span>
                          <button
                            onClick={() => removeSignature(sig.id)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {!showSignature && !signatureData && (
                    <button
                      onClick={() => setShowSignature(true)}
                      className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Create Signature
                    </button>
                  )}
                  
                  {!showSignature && signatureData && (
                    <div className="space-y-2">
                      <div className="p-2 border border-gray-300 rounded">
                        <img src={signatureData} alt="Your signature" className="w-full h-20 object-contain" />
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setSignatureData(null);
                            setShowSignature(true);
                          }}
                          className="flex-1 px-3 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm"
                        >
                          Redo
                        </button>
                        <button
                          onClick={addNewSignature}
                          disabled={isPlacingSignature}
                          className="flex-1 px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400 text-sm"
                        >
                          {isPlacingSignature ? 'Click PDF...' : 'Add to PDF'}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {showSignature && (
                    <div className="space-y-2">
                      <div className="border border-gray-300 rounded">
                        <SignatureCanvas
                          ref={signatureRef}
                          canvasProps={{
                            width: 300,
                            height: 150,
                            className: 'signature-canvas w-full'
                          }}
                          backgroundColor="white"
                        />
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={handleClearSignature}
                          className="flex-1 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          Clear
                        </button>
                        <button
                          onClick={handleSaveSignature}
                          className="flex-1 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        >
                          Save Signature
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Email Notifications Section */}
                <div className="pt-4 border-t">
                  <h3 className="text-lg font-semibold mb-3">📧 Email Notifications</h3>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notify when signed (optional)
                      </label>
                      <input
                        type="email"
                        value={notifyEmail}
                        onChange={(e) => setNotifyEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="admin@example.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Get notified when this document is signed
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Next signer email (optional)
                      </label>
                      <input
                        type="email"
                        value={nextSignerEmail}
                        onChange={(e) => setNextSignerEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="nextsigner@example.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Automatically notify the next person who needs to sign
                      </p>
                    </div>
                    
                    {nextSignerEmail && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Next signer name
                          </label>
                          <input
                            type="text"
                            value={nextSignerName}
                            onChange={(e) => setNextSignerName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="John Doe"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Message to next signer (optional)
                          </label>
                          <textarea
                            value={notificationMessage}
                            onChange={(e) => setNotificationMessage(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Please review and sign this document at your earliest convenience."
                            rows={2}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
                
                <button
                  onClick={handleSubmitSignedPDF}
                  disabled={signatures.length === 0 || !signerName || !signerEmail || isSigning}
                  className="w-full px-4 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isSigning ? 'Signing...' : `Sign & Download PDF (${signatures.length} signature${signatures.length !== 1 ? 's' : ''})`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFSigner;